/**
 * Converts a string to kebab case.
 * @param {string} str - The input string.
 * @returns {string} - The kebab-cased string.
 */
export const kebabify = (str: string): string => {
  return str
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/([a-z])([A-Z])/g, '$1-$2') // Add hyphen between camelCase words
    .replace(/_/g, '-') // Replace underscores with hyphens
    .toLowerCase() // Convert to lowercase
}

export const getStringValue = (value: string | null) => {
  return value ? value.toString().toLowerCase() : ''
}

/**
 * Decodes a URI component, returning the raw value when it contains malformed
 * escape sequences rather than throwing a URIError.
 * @param {string} value - The possibly percent-encoded input string.
 * @returns {string} - The decoded string, or the input when it cannot be decoded.
 */
export const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const EMOJI_PRESENTATION_SELECTOR = /\uFE0F/g

/**
 * Removes variation selector-16 so symbols such as ® render with the text font
 * instead of the color emoji font, which ignores the inherited text color.
 * @param {string} text - The input string.
 * @returns {string} - The string with emoji presentation selectors removed.
 */
export const stripEmojiPresentation = (text: string): string =>
  text ? text.replace(EMOJI_PRESENTATION_SELECTOR, '') : text

// Symbols screen readers already pronounce sensibly ($ % . , - @ ! ? :) are
// intentionally left out - only symbols that read as noise or gibberish belong here.
const SYMBOL_TO_WORD: Record<string, string> = {
  '/': ' per ',
  '+': ' plus ',
  '×': ' times ',
  '~': ' about ',
  '•': ' and ',
  '·': ' and ',
  '|': ' or ',
  '°': ' degrees ',
  '™': '',
  '®': '',
  '©': '',
  '#': ' number '
}

export const convertSymbolsToWords = (text: string): string => {
  if (!text) return text

  const converted = Object.entries(SYMBOL_TO_WORD).reduce(
    (result, [symbol, word]) => result.split(symbol).join(word),
    text
  )

  return converted.replace(/\s+/g, ' ').trim()
}
